import { PackOrder } from "@/types/packing";
import { Logistics, Shipment, AddLogistics as TAddLogistics } from "@/types/shipment";
import { Modal, Form, Select, Input, InputNumber, notification, Spin, Typography } from "antd";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectShipMethod from "@/components/Selector/SelectShipMethod";
import { logisticsAdd, shipmentPack } from "@/apis/shipment";
import SelectCurrencyVal from "@/components/Selector/SelectCurrencyVal";

interface BaseProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (newLogi: Logistics) => void;
};

interface ShipmentProps {
  shipment: Shipment;
  pack: null;
};
interface PackProps {
  shipment: null;
  pack: PackOrder;
}
type AddLogisticsProps = BaseProps & (ShipmentProps | PackProps);

const AddLogistics: FC<AddLogisticsProps> = ({ open, shipment, pack, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<TAddLogistics>();
  const [loading, setLoading] = useState(false);
  const [packData, setPackData] = useState<PackOrder | null>(pack);

  useEffect(() => {
    const initForm = async () => {
      if (open) {
        form.resetFields();
        if (shipment) {
          try {
            // setLoading(true);
            // shipment 模式：需要获取 packingOrderId
            // const packOrder = await shipmentPack(shipment.id, "fetch");
            // setPackData(packOrder);
            // const packingOrderId = packOrder?.id || "";
            // if (!packingOrderId) {
            //   notification.error({ message: t('shipment.shipmentNoPack') });
            // }
            form.setFieldsValue({
              packingOrderId: shipment.packingOrderId ?? '',
              shipmentOrderId: shipment.id,
            });
            // setLoading(false);
          } catch (error) {
            notification.error({ message: t('shipment.getPackErr') })
            setLoading(false);
          }
        } else if (pack) {
          // pack 模式：直接使用 pack 的 ID
          const shipmentOrderId = pack.shipmentOrderId || "";
          if (!shipmentOrderId) {
            notification.error({ message: t('shipment.packNoShipment') });
          }
          form.setFieldsValue({
            packingOrderId: pack.id,
            shipmentOrderId,
          });
        }
      }
    };
    initForm();
  }, [open, shipment, pack, form, t]);

  const handleOk = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      setLoading(true);

      // 分开验证 packingOrderId 和 shipmentOrderId
      if (!values.packingOrderId) {
        notification.error({ message: t('shipment.shipmentNoPack') });
        setLoading(false);
        return;
      }
      if (!values.shipmentOrderId) {
        notification.error({ message: t('shipment.packNoShipment') });
        setLoading(false);
        return;
      }

      let data = await logisticsAdd(values as TAddLogistics);
      setLoading(false);
      notification.success({ message: t('shipment.addSuccess') });
      form.resetFields();
      onSuccess(data);
    } catch (error) {
      setLoading(false);
      let desc: string | undefined = undefined;
      if (error === 74118) desc = t('shipment.hasLogis')
      notification.error({ message: t('shipment.addError'), description: desc });
    }
  };

  return (
    <Modal
      title={t('shipment.addLogistics')}
      width={800}
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okButtonProps={{ disabled: loading }}
      maskClosable={false}
    >
      <Spin spinning={loading}>
        {shipment && <Typography.Title level={5} style={{ margin: 0, marginBottom: 4 }}>{t('shipment.number')}: {shipment.number}</Typography.Title>}
        {packData && <Typography.Title level={5} style={{ margin: 0, marginBottom: 4 }}>{t('warehouse.packingNo')}: {packData.number}</Typography.Title>}
        <Form<TAddLogistics> form={form}>
          <Form.Item name="packingOrderId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="shipmentOrderId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="shipMethod"
            label={t('shipment.shipMethod')}
            rules={[{ required: true }]}
          >
            <SelectShipMethod />
          </Form.Item>

          <Form.Item
            name="carrier"
            label={t('shipment.shipLogistics')}
            rules={[{ required: true }]}
          >
            <Input placeholder={t('shipment.plCarrier')} />
          </Form.Item>

          <Form.Item
            name="trackingNo"
            label={t('shipment.trackingNo')}
            rules={[{ required: true }]}
          >
            <Input placeholder={t('shipment.plTrackingNo')} />
          </Form.Item>

          <Form.Item
            name="blNo"
            label={t('shipment.blNo')}
          >
            <Input placeholder={t('shipment.plBlNo')} />
          </Form.Item>

          <Form.Item
            name="vesselVoyage"
            label={t('shipment.vesselVoyage')}
          >
            <Input placeholder={t('shipment.plVesselVoyage')} />
          </Form.Item>

          <Form.Item
            name="containerNo"
            label={t('shipment.containerNo')}
          >
            <Input placeholder={t('shipment.plContainerNo')} />
          </Form.Item>

          <Form.Item
            name="freightCost"
            label={t('shipment.freightCost')}
          >
            <InputNumber changeOnWheel style={{ width: "100%" }} min={0} placeholder={t('shipment.plFreightCost')} />
          </Form.Item>

          <Form.Item
            name="freightCurrency"
            label={t('shipment.freightCurrency')}
            normalize={value => value?.toUpperCase().replace(/[^A-Z]/g, '')}
          >
            <SelectCurrencyVal />
          </Form.Item>

          <Form.Item name="remark" label={t('shipment.remark')}>
            <Input.TextArea rows={3} maxLength={1000} showCount placeholder={t('shipment.plRemark')} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddLogistics;